<script setup lang="ts">
import type { Category, ProductLine } from '~/types'

const props = defineProps<{
  categories: Category[]
  productLines: ProductLine[]
  componentTypes: string[]
}>()

const emit = defineEmits<{
  filtersChange: []
}>()

const { filters, setFilter, clearFilters, hasActiveFilters } = useFilters()

// Handle filter changes - convert 'all' back to undefined
const handleCategoryChange = (categoryId: string | undefined) => {
  setFilter('categoryId', categoryId === 'all' ? undefined : categoryId)
  emit('filtersChange')
}

const handleProductLineChange = (productLineId: string | undefined) => {
  setFilter('productLineId', productLineId === 'all' ? undefined : productLineId)
  emit('filtersChange')
}

const handleComponentTypeChange = (componentType: string | undefined) => {
  setFilter('componentType', componentType === 'all' ? undefined : componentType)
  emit('filtersChange')
}

const handleClear = () => {
  clearFilters()
  emit('filtersChange')
}

// Transform arrays to select options - use 'all' instead of empty string
const categoryOptions = computed(() => [
  { label: 'Todas las categorías', value: 'all' },
  ...props.categories.map(c => ({ label: c.name, value: c.id }))
])

const productLineOptions = computed(() => [
  { label: 'Todas las líneas', value: 'all' },
  ...props.productLines.map(l => ({ label: l.name, value: l.id }))
])

const componentTypeOptions = computed(() => [
  { label: 'Todos los tipos', value: 'all' },
  ...props.componentTypes.map(t => ({ label: t, value: t }))
])
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <h3 class="font-semibold text-gray-900 dark:text-white">Filtros</h3>
      <UButton
        v-if="hasActiveFilters"
        variant="ghost"
        color="neutral"
        size="xs"
        @click="handleClear"
      >
        Limpiar
      </UButton>
    </div>

    <!-- Category filter -->
    <UFormField label="Categoría">
      <USelect
        :model-value="filters.categoryId || 'all'"
        :items="categoryOptions"
        value-key="value"
        class="w-full"
        @update:model-value="handleCategoryChange"
      />
    </UFormField>

    <!-- Product Line filter -->
    <UFormField label="Línea de Producto">
      <USelect
        :model-value="filters.productLineId || 'all'"
        :items="productLineOptions"
        value-key="value"
        class="w-full"
        @update:model-value="handleProductLineChange"
      />
    </UFormField>

    <!-- Component Type filter -->
    <UFormField label="Tipo de Componente">
      <USelect
        :model-value="filters.componentType || 'all'"
        :items="componentTypeOptions"
        value-key="value"
        class="w-full"
        @update:model-value="handleComponentTypeChange"
      />
    </UFormField>
  </div>
</template>
